import { useEffect, useMemo, useState } from "react";
import { useTenant } from "../../Contexts/TenantContext";
import { productService } from "../../Api/Services/productService";
import { GetProduct } from "../../Models/Product";
import { CommercialUnit } from "../../Enums/CommercialUnit";
import { LengthUnit } from "../../Enums/LengthUnit";
import { WeightUnit } from "../../Enums/WeightUnit";
import { formatPriceBRL } from "../../Utils/DataFormatters";
import { getProductOriginOptions, getProductCommercialUnitLabel } from "../../Utils/Enums";
import { toast } from "react-toastify";
import Modal from "../common/Modal/Modal";
import Button from "../Button/Button";

interface AdminProductViewModalProps {
  productId: string | null;
  onClose: () => void;
  onEdit?: (productId: string) => void;
}

const AdminProductViewModal = ({ productId, onClose, onEdit }: AdminProductViewModalProps) => {
  const { tenantPreference } = useTenant();
  const showFiscalNoteSettings = tenantPreference?.showFiscalNoteSettings ?? true;
  const enableBrandsFeature = tenantPreference?.enableBrandsFeature ?? true;
  const enableCategoriesFeature = tenantPreference?.enableCategoriesFeature ?? true;
  const enableModelsFeature = tenantPreference?.enableModelsFeature ?? true;
  const enableProductComponents = tenantPreference?.enableProductComponents ?? true;

  const [product, setProduct] = useState<GetProduct | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!productId) {
      setProduct(null);
      return;
    }
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const data = await productService.get(productId);
        if (!data) {
          toast.error("Produto não encontrado.");
          onClose();
          return;
        }
        setProduct(data);
      } catch {
        toast.error("Produto não encontrado.");
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  const images = useMemo(
    () => [product?.productImageOne, product?.productImageTwo, product?.productImageThree, product?.productImageFour].filter(Boolean),
    [product]
  );

  const documents = useMemo(
    () => [product?.productDocumentOne, product?.productDocumentTwo].filter(Boolean),
    [product]
  );

  const originLabel = useMemo(
    () => getProductOriginOptions().find(o => o.value === product?.origin)?.label ?? "N/I",
    [product?.origin]
  );

  const getLengthUnitLabel = (unit: LengthUnit | null | undefined) => {
    if (unit === LengthUnit.M) {
      return "m";
    }
    if (unit === LengthUnit.MM) {
      return "mm";
    }
    return "cm";
  };

  const getWeightLabel = () => {
    if (product?.weightValue == null) {
      return null;
    }
    const unit = product.weightUnit === WeightUnit.G ? "g" : "kg";
    return `${product.weightValue} ${unit}`;
  };

  const getDimensionLabel = (value: number | null | undefined, unit: LengthUnit | null | undefined) => {
    if (value == null) {
      return null;
    }
    return `${value} ${getLengthUnitLabel(unit)}`;
  };

  const hasDimensions = product != null && (
    product.weightValue != null ||
    product.heightValue != null ||
    product.widthValue != null ||
    product.lengthValue != null
  );

  return (
    <Modal isOpen={!!productId} onClose={onClose} maxWidth="max-w-3xl" title={"Detalhes do produto"}>
      {loading ? (
        <p className="text-center py-8 text-gray-500">{"Carregando produto..."}</p>
      ) : product ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><p className="text-sm text-gray-500">{"Nome"}</p><p className="font-semibold text-gray-800">{product.name}</p></div>
            <div><p className="text-sm text-gray-500">{"Código"}</p><p className="font-semibold text-gray-800">{product.code}</p></div>
            <div><p className="text-sm text-gray-500">{"Preço"}</p><p className="font-semibold text-gray-800">{formatPriceBRL(product.price)}</p></div>
            <div><p className="text-sm text-gray-500">{"Estoque"}</p><p className="font-semibold text-gray-800">{product.stockQuantity}</p></div>
            <div><p className="text-sm text-gray-500">{"Ativo"}</p><p className="font-semibold text-gray-800">{product.isActive ? "Sim" : "Não"}</p></div>
            {enableBrandsFeature && (
              <div><p className="text-sm text-gray-500">{"Marca"}</p><p className="font-semibold text-gray-800">{product.brand?.name ?? "N/I"}</p></div>
            )}
            {enableCategoriesFeature && (
              <div><p className="text-sm text-gray-500">{"Categoria"}</p><p className="font-semibold text-gray-800">{product.category?.name ?? "N/I"}</p></div>
            )}
            {enableModelsFeature && (
              <div><p className="text-sm text-gray-500">{"Modelo"}</p><p className="font-semibold text-gray-800">{product.model?.name ?? "N/I"}</p></div>
            )}
          </div>

          <div>
            <h3 className="text-base font-semibold text-gray-800 mb-1">{"Descrição"}</h3>
            <p className="text-gray-700 whitespace-pre-line">{product.description || "N/I"}</p>
          </div>

          {hasDimensions && (
            <div>
              <h3 className="text-base font-semibold text-gray-800 mb-2">{"Dimensões e peso"}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {getWeightLabel() != null && (
                  <div><p className="text-sm text-gray-500">{"Peso"}</p><p className="font-semibold text-gray-800">{getWeightLabel()}</p></div>
                )}
                {getDimensionLabel(product.heightValue, product.heightUnit) != null && (
                  <div><p className="text-sm text-gray-500">{"Altura"}</p><p className="font-semibold text-gray-800">{getDimensionLabel(product.heightValue, product.heightUnit)}</p></div>
                )}
                {getDimensionLabel(product.widthValue, product.widthUnit) != null && (
                  <div><p className="text-sm text-gray-500">{"Largura"}</p><p className="font-semibold text-gray-800">{getDimensionLabel(product.widthValue, product.widthUnit)}</p></div>
                )}
                {getDimensionLabel(product.lengthValue, product.lengthUnit) != null && (
                  <div><p className="text-sm text-gray-500">{"Comprimento"}</p><p className="font-semibold text-gray-800">{getDimensionLabel(product.lengthValue, product.lengthUnit)}</p></div>
                )}
              </div>
            </div>
          )}

          {showFiscalNoteSettings && (
            <div>
              <h3 className="text-base font-semibold text-gray-800 mb-2">{"Definições para Nota Fiscal"}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><p className="text-sm text-gray-500">{"Origem"}</p><p className="font-semibold text-gray-800">{originLabel}</p></div>
                <div><p className="text-sm text-gray-500">{"Unidade comercial"}</p><p className="font-semibold text-gray-800">{getProductCommercialUnitLabel(product.unit as CommercialUnit)}</p></div>
                <div><p className="text-sm text-gray-500">{"Unidades por caixa"}</p><p className="font-semibold text-gray-800">{product.unitsPerBoxText || "N/I"}</p></div>
                <div><p className="text-sm text-gray-500">NCM</p><p className="font-semibold text-gray-800">{product.ncm || "N/I"}</p></div>
                <div><p className="text-sm text-gray-500">{"Código de barras"}</p><p className="font-semibold text-gray-800">{product.barcode || "N/I"}</p></div>
              </div>
            </div>
          )}

          <div>
            <h3 className="text-base font-semibold text-gray-800 mb-2">{"Especificações"}</h3>
            <table className="w-full border-collapse border border-gray-300 text-left">
              <thead><tr className="bg-primary-120"><th className="border p-3">{"Chave"}</th><th className="border p-3">{"Valor"}</th></tr></thead>
              <tbody>
                {product.specifications?.length ? product.specifications.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50"><td className="border p-3">{s.key}</td><td className="border p-3">{s.value}</td></tr>
                )) : <tr><td className="border p-3 text-center text-gray-500" colSpan={2}>{"Nenhuma especificação cadastrada."}</td></tr>}
              </tbody>
            </table>
          </div>

          {enableProductComponents && (
            <div>
              <h3 className="text-base font-semibold text-gray-800 mb-2">{"Peças"}</h3>
              <table className="w-full border-collapse border border-gray-300 text-left">
                <thead><tr className="bg-primary-120"><th className="border p-3 text-center">{"Código"}</th><th className="border p-3">{"Nome"}</th></tr></thead>
                <tbody>
                  {product.components?.length ? product.components.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50"><td className="border p-3 text-center">{c.component.code}</td><td className="border p-3">{c.component.name}</td></tr>
                  )) : <tr><td className="border p-3 text-center text-gray-500" colSpan={2}>{"Nenhuma peça vinculada."}</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          <div>
            <h3 className="text-base font-semibold text-gray-800 mb-2">{"Imagens"}</h3>
            {images.length ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {images.map((img, idx) => (
                  <a key={img!.id + idx} href={img!.url} target="_blank" rel="noreferrer" className="block border rounded overflow-hidden hover:shadow">
                    <img src={img!.url} alt={`${"Imagem"} ${idx + 1}`} className="w-full h-32 object-cover" />
                  </a>
                ))}
              </div>
            ) : <p className="text-gray-600 text-sm">{"Nenhuma imagem enviada."}</p>}
          </div>

          {enableProductComponents && (
            <div>
              <h3 className="text-base font-semibold text-gray-800 mb-2">{"Documentos"}</h3>
              {documents.length ? (
                <ul className="list-disc list-inside space-y-1">
                  {documents.map((doc, idx) => (
                    <li key={doc!.id + idx}>
                      <a href={doc!.url} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">{"Documento"} {idx + 1}</a>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-gray-600 text-sm">{"Nenhum documento enviado."}</p>}
            </div>
          )}

          {onEdit && (
            <div className="flex justify-end pt-2">
              <Button onClick={() => onEdit(product.id)} className="px-4 py-2">
                {"Editar"}
              </Button>
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  );
};

export default AdminProductViewModal;
